/**
 * WbsTemplateEditor — admin tool to manage the WBS milestone templates that drive in-app scaffolding
 * (Supabase wbs_templates). Moves template upkeep out of Airtable. See docs/WBS_SCAFFOLDING.md.
 *
 * Opened via `open-wbs-editor`; mounted once in OutlineView; gated to admin. Grouped by phase; each row
 * = one template milestone (deliverable type, subtitle, default duration, predecessor set, order).
 * Edits persist immediately (on change/blur) through the admin-gated /api/wbs route.
 *
 * A row can list MULTIPLE predecessor candidates (predecessor_ids) — not an AND-dependency, and no
 * ranking to set: some phases are optional per project (e.g. Discretionary Approvals), so at scaffold
 * time whichever candidate's phase actually exists for the project is used; if more than one exists,
 * the one with the higher Sort Order (i.e. later in the template) wins, since that's the real,
 * later-occurring dependency. See PredecessorCell below + api/_scaffoldMilestones.js.
 *
 * Timeline visualizer (2026-09-08): the milestone list here is the same view it's always been — a
 * timeline panel was added to its right, drawn by the SAME Gantt engine the real per-project Gantt
 * uses (gantt/GanttTimeline.jsx + lib/ganttLayout.js), so a fix to either one lands on both. A WBS
 * template has no calendar dates, only touch-time/wait durations and a predecessor graph, so
 * lib/simulateWbsSchedule.js synthesizes a plausible schedule (anchored at today) to feed the shared
 * renderer. The timeline is READ-ONLY — editing stays exactly where it already was, in the fields
 * below; the chart is a live reflection of them, not a second place to change them.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { listWbsTemplates, createWbsTemplate, updateWbsTemplate, deleteWbsTemplate } from '../services/wbs.js'
import { listMilestoneTypeSettings } from '../services/milestoneTypes.js'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { ActionMenu, useActionMenu } from './ActionMenu.jsx'
import GanttTimeline, { ZOOMS, PRIMARY, TEXT, ROW_HILITE } from './gantt/GanttTimeline.jsx'
import { buildPhaseLayout, buildWindow, todayUTC } from '../lib/ganttLayout.js'
import { simulateWbsSchedule, computeTemplateBuffer } from '../lib/simulateWbsSchedule.js'

// All phases that can carry a WBS template (the Airtable WBS Phase single-select), in schedule order.
const PHASES = [
  'Business Development Phase', 'Design Consultation Phase', 'Pre-design & Planning Phase',
  'Existing Building 3D Model', 'Schematic Design Phase', 'Discretionary Approvals Phase',
  'Design Development Phase', 'Construction Documents Phase', 'Permitting Phase',
  'Architectural Detailing Phase', 'Interior Architecture Phase', 'Bidding & Negotiation Phase',
  'Construction Support Phase',
]

const ROW_H = 26

export default function WbsTemplateEditor() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [types, setTypes] = useState([])
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)
  const [zoom, setZoom] = useState('days')
  const [collapsedPhases, setCollapsedPhases] = useState(() => new Set())
  const [hoverRowId, setHoverRowId] = useState(null)

  // Drag-to-reorder (and drag-across-phase) state. `rowRefs` holds each visible ms row's DOM node
  // (keyed by row id) so a reorder can FLIP-animate every other row out of the way; `flipRef` carries
  // the "before" top positions captured just before a reordering setRows(), consumed by the
  // useLayoutEffect below on the very next paint. Pattern lifted from SiteAnalysisModal's Objects
  // panel (docs/SITE_ANALYSIS.md — "Illustrator-style Objects panel with drag-handle reorder").
  const rowRefs = useRef({})
  const flipRef = useRef(null)
  const [dragRow, setDragRow] = useState(null) // { id, rect, dy } | null

  useLayoutEffect(() => {
    const before = flipRef.current
    if (!before) return
    flipRef.current = null
    Object.keys(before).forEach(id => {
      const el = rowRefs.current[id]
      if (!el) return
      const delta = before[id] - el.getBoundingClientRect().top
      if (!delta) return
      el.style.transition = 'none'
      el.style.transform = 'translateY(' + delta + 'px)'
      requestAnimationFrame(() => { el.style.transition = 'transform 180ms ease'; el.style.transform = '' })
    })
  })

  useEffect(() => {
    function onOpen() { setOpen(true); load() }
    window.addEventListener('open-wbs-editor', onOpen)
    return () => window.removeEventListener('open-wbs-editor', onOpen)
  }, [])

  async function load() {
    setLoading(true); setError(null)
    try {
      const [r, k] = await Promise.all([listWbsTemplates(), listMilestoneTypeSettings().catch(() => ({ settings: {}, typical: {} }))])
      if (r.error) throw new Error(r.error)
      setRows(r.rows || []); setTypes(r.types || []); setKinds(k || { settings: {}, typical: {} })
    } catch (e) { setError(e.message || 'Failed to load') }
    finally { setLoading(false) }
  }
  // Build step 6a: "is this a wait" comes from the milestone type's own setting (a feeder), not a
  // per-row checkbox. A feeder row's length is our typical time, set on the type page, not typed here.
  const [kinds, setKinds] = useState({ settings: {}, typical: {} })
  const feederOf = (typeId) => {
    const s = (kinds.settings || {})[typeId]
    if (!s || s.kind !== 'feeder') return null
    const t = (kinds.typical || {})[s.owedBy]
    return { owedBy: s.owedBy, typicalDays: t ? t.days : null }
  }

  function close() { setOpen(false); setRows([]); setError(null) }
  function togglePhase(name) { setCollapsedPhases(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n }) }

  // Update local state immediately (so inputs stay responsive); caller persists separately.
  function setLocal(id, field, value) {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r))
  }

  async function persist(id, fields) {
    setBusyId(id)
    try {
      const r = await updateWbsTemplate(id, fields)
      if (r.error) throw new Error(r.error)
      if (r.row) setRows(prev => prev.map(x => x.id === id ? r.row : x))
    } catch (e) { await alertDialog({ title: 'Save failed', message: e.message }); load() }
    finally { setBusyId(null) }
  }

  function onType(id, typeId) {
    const name = (types.find(t => t.id === typeId) || {}).name || null
    setLocal(id, 'deliverable_type_airtable_id', typeId)
    persist(id, { deliverable_type_airtable_id: typeId || null, deliverable_type_name: name })
  }

  async function addRow(phase) {
    const nextOrder = (rows.reduce((m, r) => Math.max(m, r.sort_order || 0), 0)) + 1
    setBusyId('new')
    try {
      const r = await createWbsTemplate({ phase, sort_order: nextOrder, active: true })
      if (r.error) throw new Error(r.error)
      if (r.row) setRows(prev => [...prev, r.row])
    } catch (e) { await alertDialog({ title: 'Add failed', message: e.message }) }
    finally { setBusyId(null) }
  }

  async function removeRow(row) {
    const label = row.deliverable_type_name || 'this template milestone'
    if (!(await confirmDialog({ title: 'Delete template row?', message: 'Delete “' + label + '”? Any rows that list it as a predecessor candidate will drop it from their list.', confirmLabel: 'Delete', danger: true }))) return
    setBusyId(row.id)
    try {
      const r = await deleteWbsTemplate(row.id)
      if (r.error) throw new Error(r.error)
      setRows(prev => prev.filter(x => x.id !== row.id))
    } catch (e) { await alertDialog({ title: 'Delete failed', message: e.message }) }
    finally { setBusyId(null) }
  }

  // Drag a row's grip handle to reorder it — within its phase, or across into a different phase's
  // group entirely (dropping it next to a row reassigns it to that row's phase). Only rows in
  // currently-EXPANDED phases are reachable, since a collapsed phase renders none of its rows; those
  // hidden rows are carried through untouched.
  //
  // Index math walks a COMBINED list — every row AND every phase-header, in rendered order — not
  // just the ms rows. Phase headers render at the exact same ROW_H as a milestone row, so if the
  // index math only counted ms rows, each phase heading crossed during a drag silently ate (or
  // gave back) one row's worth of mouse movement without the drop target keeping pace — the ghost
  // would visibly drift out from under the pointer once you dragged past a phase boundary (Matt,
  // 2026-09-08). Landing immediately after a phase header means "first in that phase"; landing next
  // to an ms row means "next to that row" (inherits its phase) — same as before, just now aware a
  // neighbor can itself be a header.
  function startRowDrag(e, row) {
    e.preventDefault(); e.stopPropagation()
    const rowEl = rowRefs.current[row.id]
    if (!rowEl) return
    const rect = rowEl.getBoundingClientRect()
    const rowH = rect.height
    const startY = e.clientY
    // Map back to the plain wbs_templates rows (not `layout`'s simulated/augmented copies) so
    // setRows() below stays in the state's own shape.
    const rowById = new Map(rows.map(r => [r.id, r]))
    let combined = layout.map(n => n.kind === 'ms' ? { kind: 'ms', row: rowById.get(n.row.id) } : { kind: 'phase', pid: n.pid })
      .filter(c => c.kind !== 'ms' || c.row)
    const startIdx = combined.findIndex(c => c.kind === 'ms' && c.row.id === row.id)
    if (startIdx < 0) return
    let changed = false
    setDragRow({ id: row.id, rect, dy: 0 })

    function applyOrder(nextOrder) {
      const visibleIds = new Set(nextOrder.map(r => r.id))
      const hidden = rows.filter(r => !visibleIds.has(r.id))
      setRows([...nextOrder, ...hidden])
    }

    function move(ev) {
      const dy = ev.clientY - startY
      setDragRow(d => (d && d.id === row.id ? { ...d, dy } : d))
      const newIdx = Math.max(0, Math.min(combined.length - 1, startIdx + Math.round(dy / rowH)))
      const idxNow = combined.findIndex(c => c.kind === 'ms' && c.row.id === row.id)
      if (newIdx === idxNow) return
      changed = true
      const before = {}
      combined.forEach(c => { if (c.kind !== 'ms' || c.row.id === row.id) return; const el = rowRefs.current[c.row.id]; if (el) before[c.row.id] = el.getBoundingClientRect().top })
      flipRef.current = before

      const dragged = combined[idxNow]
      const next = [...combined]
      next.splice(idxNow, 1)
      next.splice(newIdx, 0, dragged)
      const prevNeighbor = next[newIdx - 1]
      const nextNeighbor = next[newIdx + 1]
      const newPhase = prevNeighbor ? (prevNeighbor.kind === 'phase' ? prevNeighbor.pid : prevNeighbor.row.phase)
        : (nextNeighbor ? (nextNeighbor.kind === 'phase' ? nextNeighbor.pid : nextNeighbor.row.phase) : dragged.row.phase)
      if (newPhase !== dragged.row.phase) next[newIdx] = { kind: 'ms', row: { ...dragged.row, phase: newPhase } }
      combined = next
      applyOrder(combined.filter(c => c.kind === 'ms').map(c => c.row))
    }

    function up() {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      setDragRow(null)
      if (!changed) return
      const order = combined.filter(c => c.kind === 'ms').map(c => c.row)
      const finalRow = order.find(r => r.id === row.id)
      const touchedPhases = new Set([row.phase, finalRow ? finalRow.phase : row.phase])
      touchedPhases.forEach(pid => {
        order.filter(r => r.phase === pid).forEach((r, i) => {
          const so = (i + 1) * 10
          const fields = {}
          if (r.sort_order !== so) fields.sort_order = so
          if (r.id === row.id && r.phase !== row.phase) fields.phase = r.phase
          if (Object.keys(fields).length) persist(r.id, fields)
        })
      })
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
  }

  // Recompute each phase's row order from the simulated schedule's earliest_start (stable — ties
  // keep their current relative order) and persist the new sort_order values. FLIP-animates same as
  // a drag, so it reads as a live reflow, not a page reload.
  function sortByTimeline() {
    const startOf = new Map(simRows.map(r => [r.id, r.earliest_start || '']))
    const groups = new Map()
    rows.forEach(r => { if (!groups.has(r.phase)) groups.set(r.phase, []); groups.get(r.phase).push(r) })

    const before = {}
    Object.keys(rowRefs.current).forEach(id => { const el = rowRefs.current[id]; if (el) before[id] = el.getBoundingClientRect().top })
    flipRef.current = before

    const newRows = []
    const toPersist = []
    groups.forEach((groupRows, pid) => {
      const sorted = [...groupRows].sort((a, b) => {
        const sa = startOf.get(a.id) || '', sb = startOf.get(b.id) || ''
        if (sa !== sb) return sa < sb ? -1 : 1
        return (a.sort_order || 0) - (b.sort_order || 0)
      })
      sorted.forEach((r, i) => {
        const so = (i + 1) * 10
        if (r.sort_order !== so) { toPersist.push({ id: r.id, sort_order: so }); newRows.push({ ...r, sort_order: so }) }
        else newRows.push(r)
      })
    })
    setRows(newRows)
    toPersist.forEach(u => persist(u.id, { sort_order: u.sort_order }))
  }

  // Simulated schedule (lib/simulateWbsSchedule.js) — synthetic dates from touch-time/wait durations
  // + the predecessor graph, anchored at today, so the shared Gantt engine has something to draw.
  // Used for BOTH this column's own row positions (so it lines up 1:1 with the timeline) and the
  // timeline itself — one computation, no drift between the two.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { rows: simRows, deps: simDeps } = useMemo(() => simulateWbsSchedule(rows.map(r => ({ ...r, is_wait: !!feederOf(r.deliverable_type_airtable_id) || !!r.is_wait }))), [rows, kinds])
  const templateBuffer = useMemo(() => computeTemplateBuffer(rows), [rows])
  const phaseKeyFor = (r) => r.phase
  const phaseLabelFor = (k) => k
  const PX = ZOOMS[zoom].px
  const { xOf, timelineW } = buildWindow(simRows, PX, todayUTC())
  // lineNumMap covers every row regardless of collapse state (buildPhaseLayout numbers the whole
  // template, not just what's currently expanded) — needed so the predecessor picker can show and
  // search by a candidate's # even when its own phase is collapsed in the list on the left.
  const { layout, lineNumMap } = buildPhaseLayout({
    rows: simRows, phaseKeyFor, phaseLabelFor, orderedKeys: PHASES,
    collapsedKeys: collapsedPhases, rowH: ROW_H, xOf, px: PX,
  })

  if (!open) return null
  if (isSeatLoaded() && !hasModule('admin') && !hasModule('checklist-templates')) {
    return (
      <>
        <div style={S.backdrop} onClick={close} />
        <div style={S.modal}><div style={S.restricted}>Only people who can edit templates can open this.</div><div style={S.btns}><button style={S.ghost} onClick={close}>Close</button></div></div>
      </>
    )
  }

  return (
    <>
      <div style={S.backdrop} onClick={close} />
      <div style={S.modal} role="dialog" aria-label="WBS Templates">
        <div style={S.headRow}>
          <div>
            <span style={S.kicker}>WBS Templates</span>
            <div style={S.sub}>The milestone template per phase — what gets created when a phase is scaffolded. Edits save immediately and apply to the next scaffold (existing milestones are untouched). The timeline to the right is a simulated schedule (touch time + predecessors, anchored at today) — read-only, edit the fields on the left.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {templateBuffer.found && (
              <span style={S.bufferReadout} title="Worst-case ceiling, not a promise — every optional phase (Discretionary Approvals, Interior Architecture, …) is counted as if it always applies, which no single real project's chain does. Same formula the real Gantt's buffer readout uses: 0.5 × chain touch-time × 4.2 flow-time factor.">
                Implied project buffer (ceiling): <strong>{Math.round(templateBuffer.bufferDays)}d</strong>
              </span>
            )}
            <button style={S.addBtn} onClick={sortByTimeline} title="Recompute row order per phase from the simulated schedule's start dates">Sort by timeline</button>
            <div style={{ display: 'flex', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, overflow: 'hidden' }}>
              {Object.entries(ZOOMS).map(([k, z]) => (
                <button key={k} onClick={() => setZoom(k)}
                  style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '4px 10px', border: 'none', cursor: 'pointer',
                    background: zoom === k ? PRIMARY : 'var(--aj-bg-elevated)', color: zoom === k ? '#fff' : 'var(--aj-text-mid)' }}>{z.label}</button>
              ))}
            </div>
            <button style={S.x} onClick={close} aria-label="Close">×</button>
          </div>
        </div>
        {error && <div style={S.err}>{error}</div>}

        <div style={S.body}>
          {loading ? <div style={S.muted}>Loading…</div> : (
            <div style={{ display: 'flex', minWidth: LABEL_W + timelineW }}>
              <datalist id="wbs-groups">{[...new Set(rows.map(r => r.group_name).filter(Boolean))].map(g => <option key={g} value={g} />)}</datalist>
              {/* Left column — the WBS row list, one continuous scroll with the timeline */}
              <div style={{ width: LABEL_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, background: 'var(--aj-bg-subtle)', borderRight: '0.5px solid var(--aj-border)' }}>
                <div style={{ height: HEAD_H, borderBottom: '0.5px solid var(--aj-border)', display: 'flex', alignItems: 'center', padding: '0 8px', fontFamily: 'Montserrat,sans-serif', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' }}>
                  <span style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>#</span>
                  <span style={{ flex: 1.4 }}>Milestone type</span>
                  <span style={{ flex: 1 }}>Subtitle</span>
                  <span style={{ flex: 0.8 }} title="Milestones that belong together. Deleting one offers the rest of its group on that project.">Group</span>
                  <span style={{ width: 108, textAlign: 'center', flexShrink: 0 }}>Touch days</span>
                  <span style={{ width: 40, textAlign: 'center', flexShrink: 0 }} title="Only on projects with a Discretionary Approvals phase">Only</span>
                  <span style={{ flex: 1.3 }}>Predecessor</span>
                  <span style={{ width: 20, flexShrink: 0 }} />
                </div>
                {layout.map((node, i) => node.kind === 'phase' ? (
                  <div key={'p' + node.pid + i} style={{ height: ROW_H, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', background: 'var(--aj-bg-muted)', borderBottom: '0.5px solid var(--aj-border)' }}>
                    <span onClick={() => togglePhase(node.pid)} style={{ fontSize: 10, color: 'var(--aj-text-mid)', width: 16, flexShrink: 0, textAlign: 'center', cursor: 'pointer' }}>{node.collapsed ? '▶' : '▼'}</span>
                    <span onClick={() => togglePhase(node.pid)} style={{ flex: 1, fontFamily: 'Montserrat,sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{node.name}</span>
                    <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--aj-text-mid)' }}>{node.count}</span>
                    <button style={S.addBtn} disabled={busyId === 'new'} onClick={() => addRow(node.pid)}>+ Add</button>
                  </div>
                ) : (
                  <WbsRow key={node.row.id} row={node.row} rows={rows} phase={node.row.phase} types={types} feeder={feederOf(node.row.deliverable_type_airtable_id)}
                    lineNum={node.lineNum} lineNumMap={lineNumMap} dragging={dragRow && dragRow.id === node.row.id}
                    rowRef={el => { rowRefs.current[node.row.id] = el }} onDragStart={startRowDrag}
                    busy={busyId === node.row.id} hovered={hoverRowId === node.row.id}
                    onHover={setHoverRowId}
                    onType={onType} setLocal={setLocal} persist={persist} removeRow={removeRow} />
                ))}
              </div>

              {/* Timeline — the shared Gantt chart component; see gantt/GanttTimeline.jsx */}
              <GanttTimeline
                rows={simRows} deps={simDeps}
                phaseKeyFor={phaseKeyFor} phaseLabelFor={phaseLabelFor}
                orderedPhaseKeys={PHASES} collapsedKeys={collapsedPhases}
                zoom={zoom} rowH={ROW_H} headH={HEAD_H}
                hoveredRowId={hoverRowId} onHoverRow={setHoverRowId}
                showCritical={false}
              />

              {dragRow && (() => {
                const r = rows.find(x => x.id === dragRow.id)
                if (!r) return null
                const label = (r.deliverable_type_name || '(untitled)') + (r.subtitle ? ' — ' + r.subtitle : '')
                return (
                  <div style={{
                    position: 'fixed', left: dragRow.rect.left, top: dragRow.rect.top + dragRow.dy,
                    width: dragRow.rect.width, height: dragRow.rect.height,
                    display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', boxSizing: 'border-box',
                    background: 'var(--aj-bg-elevated)', border: '0.5px solid ' + PRIMARY, borderRadius: 4,
                    boxShadow: '0 6px 18px rgba(7,39,45,0.28)', pointerEvents: 'none', zIndex: 800,
                    fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{label}</div>
                )
              })()}
            </div>
          )}
        </div>

        <div style={S.btns}><button style={S.ghost} onClick={close}>Done</button></div>
      </div>
    </>
  )
}

// One WBS row's editable fields — order #, milestone type, subtitle, touch-time/wait duration,
// predecessor candidates, delete. Unchanged content from before the timeline visualizer; just given
// a fixed ROW_H height so it lines up with the shared timeline's rows.
function WbsRow({ row, rows, phase, types, feeder, lineNum, lineNumMap, dragging, rowRef, onDragStart, busy, hovered, onHover, onType, setLocal, persist, removeRow }) {
  const phaseIdx = PHASES.indexOf(phase)
  // A predecessor can be this phase's own row or any EARLIER phase's row (schedule order) — not later.
  const predPhases = PHASES.slice(0, Math.max(phaseIdx, 0) + 1)
  return (
    <div ref={rowRef} onMouseEnter={() => onHover(row.id)} onMouseLeave={() => onHover(h => h === row.id ? null : h)}
      style={{ height: ROW_H, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', borderBottom: '0.5px solid var(--aj-border)', background: hovered ? ROW_HILITE : 'transparent', opacity: busy ? 0.5 : (dragging ? 0.35 : 1) }}>
      <span onPointerDown={e => onDragStart(e, row)} title="Drag to reorder or move to another phase"
        style={{ width: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-mid)', cursor: 'grab', userSelect: 'none' }}>
        <span style={{ fontSize: 10, color: 'var(--aj-text-muted)' }}>⠿</span>{lineNum}
      </span>
      <select style={{ ...S.inp, flex: 1.4, minWidth: 0 }} value={row.deliverable_type_airtable_id || ''} onChange={e => onType(row.id, e.target.value)}>
        <option value="">— select type —</option>
        {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <input style={{ ...S.inp, flex: 1, minWidth: 0 }} placeholder="—" value={row.subtitle || ''}
        onChange={e => setLocal(row.id, 'subtitle', e.target.value)}
        onBlur={e => persist(row.id, { subtitle: e.target.value || null })} />
      {/* Group (2026-09-24): milestones that belong together, e.g. "Public hearing". Deleting one
          offers the rest of its group on that project. Set per TYPE, so it is written to every row of
          this type at once. */}
      <input style={{ ...S.inp, flex: 0.8, minWidth: 0 }} placeholder="—" list="wbs-groups" value={row.group_name || ''}
        title="Milestones that belong together. Deleting one offers the rest of its group on that project."
        onChange={e => setLocal(row.id, 'group_name', e.target.value)}
        onBlur={e => {
          const v = e.target.value.trim() || null
          rows.filter(r => r.deliverable_type_airtable_id && r.deliverable_type_airtable_id === row.deliverable_type_airtable_id)
            .forEach(r => { if (r.id !== row.id) setLocal(r.id, 'group_name', v); persist(r.id, { group_name: v }) })
          if (!row.deliverable_type_airtable_id) persist(row.id, { group_name: v })
        }} />
      {/* Touch time is picked from a short Fibonacci ladder, not typed (Matt, 2026-09-07): arguing 6
          vs 7 days is where padding hides. A WAIT row (Client Review, Agency Review — is_wait,
          2026-09-07) is a different kind of number: a measured elapsed-day median, not a hands-on-work
          Fibonacci pick, so it gets a plain numeric field instead of the ladder. Side-by-side, not
          stacked (2026-09-08 crowding fix) — stacked overflowed the row's fixed height, which has to
          match the shared timeline's ROW_H exactly or the two columns drift apart going down the list. */}
      <div style={{ ...S.durCell, width: 108, flexShrink: 0 }} title={(feeder || row.is_wait) ? 'A wait: calendar days someone else takes (client, agency, bidders, contractor). Picked from the Fibonacci ladder; Build Ready and project setup read it from here.' : 'Touch time — Fibonacci ladder'}>
        {(feeder || row.is_wait) ? (
          // WAITS ARE FIBONACCI TOO (Matt, 2026-10-26: "use fibonacci instead of measured history. Our
          // history isn't reliable enough"): a wait row's length is picked in calendar days from the
          // longer ladder, and this template is the one place any wait length lives.
          <select style={{ ...S.inp, ...S.durInput, textAlign: 'center', fontFamily: MONO, color: '#b9770e' }} value={row.default_duration ?? ''}
            title="Wait, in calendar days"
            onChange={e => { const v = e.target.value === '' ? null : Number(e.target.value); setLocal(row.id, 'default_duration', v); persist(row.id, { default_duration: v }) }}>
            <option value="">—</option>
            {WAIT_LADDER.map(d => <option key={d} value={d}>{d} cal</option>)}
            {row.default_duration != null && !WAIT_LADDER.includes(Number(row.default_duration)) && (
              <option value={row.default_duration}>{row.default_duration} (old, pick a rung)</option>
            )}
          </select>
        ) : (
          <select style={{ ...S.inp, ...S.durInput, textAlign: 'center', fontFamily: MONO }} value={row.default_duration ?? ''}
            onChange={e => { const v = e.target.value === '' ? null : Number(e.target.value); setLocal(row.id, 'default_duration', v); persist(row.id, { default_duration: v }) }}>
            <option value="">—</option>
            {TOUCH_LADDER.map(d => <option key={d} value={d}>{d === 0.5 ? '0.5' : d}</option>)}
            {row.default_duration != null && !TOUCH_LADDER.includes(Number(row.default_duration)) && (
              <option value={row.default_duration}>{row.default_duration} (old, pick a rung)</option>
            )}
          </select>
        )}
      </div>
      {/* "Only on projects with a Discretionary Approvals phase" (requires_phase, database/268,
          2026-09-23): the discretionary review chain sits in Permitting but belongs only on
          projects that actually go through discretionary review. Hidden on the DA phase's own rows. */}
      <label title="Only create this on projects that have a Discretionary Approvals phase"
        style={{ ...S.waitToggle, width: 40, flexShrink: 0, visibility: phase === 'Discretionary Approvals Phase' ? 'hidden' : 'visible' }}>
        <input type="checkbox" checked={row.requires_phase === 'Discretionary Approvals Phase'}
          onChange={e => { const v = e.target.checked ? 'Discretionary Approvals Phase' : null; setLocal(row.id, 'requires_phase', v); persist(row.id, { requires_phase: v }) }} />
        <span style={S.waitToggleLbl}>DA</span>
      </label>
      <div style={{ flex: 1.3, minWidth: 0, height: '100%' }}>
        <PredecessorCell row={row} phase={phase} predPhases={predPhases} rows={rows} lineNumMap={lineNumMap}
          onChange={ids => { setLocal(row.id, 'predecessor_ids', ids); persist(row.id, { predecessor_ids: ids }) }} />
      </div>
      <button style={{ width: 20, flexShrink: 0, ...S.delBtn }} title="Delete" onClick={() => removeRow(row)}>×</button>
    </div>
  )
}

// A row's set of predecessors — no ranking to manage. At scaffold time, every one whose phase
// exists for the project becomes a predecessor, and the milestone starts after the LATEST of them
// finishes (Matt, 2026-09-23) — see resolvePredecessors in api/_scaffoldMilestones.js. One whose
// phase isn't in the project is simply skipped. So this is just an unordered add/remove list.
//
// A single-line summary button (fits the row's fixed ROW_H) opens a floating checkbox picker —
// replaces the old inline chip row + horizontally-scrolling strip (2026-09-08), which Matt flagged
// as hard to use. One list serves both adding and removing: check to add, uncheck to remove.
function PredecessorCell({ row, phase, predPhases, rows, lineNumMap, onChange }) {
  const menu = useActionMenu()
  const [filter, setFilter] = useState('')
  const ids = row.predecessor_ids || []
  function labelFor(id) {
    const r = rows.find(x => x.id === id)
    if (!r) return '(deleted)'
    const num = lineNumMap.get(id)
    const name = (r.deliverable_type_name || '(untitled)') + (r.subtitle ? ' — ' + r.subtitle : '')
    return (num != null ? '#' + num + ' ' : '') + (r.phase === phase ? name : r.phase + ' · ' + name)
  }
  function toggle(id) { onChange(ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]) }
  const summary = ids.length ? ids.map(labelFor).join(', ') : '— none —'
  const q = filter.trim().toLowerCase()

  return (
    <>
      <button type="button" title={summary} onClick={e => { setFilter(''); menu.toggle(e.currentTarget) }}
        style={{ ...S.predSummaryBtn, color: ids.length ? 'var(--aj-text)' : 'var(--aj-text-muted)' }}>
        {summary}
      </button>
      <ActionMenu menu={menu}>
        <div style={S.predPopover}>
          <input autoFocus type="text" placeholder="Filter by name or #…" value={filter} onChange={e => setFilter(e.target.value)} style={S.predFilterInp} />
          <div style={S.predPopoverList}>
            {predPhases.map(pn => {
              const opts = rows.filter(o => o.phase === pn && o.id !== row.id)
                .filter(o => {
                  if (!q) return true
                  const num = lineNumMap.get(o.id)
                  const nameSub = ((o.deliverable_type_name || '') + ' ' + (o.subtitle || '')).toLowerCase()
                  return nameSub.includes(q) || (num != null && String(num).includes(q))
                })
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              if (!opts.length) return null
              const label = pn === phase ? '(this phase)' : pn
              return (
                <div key={pn}>
                  <div style={S.predGroupLabel}>{label}</div>
                  {opts.map(o => {
                    const name = (o.deliverable_type_name || '(untitled)') + (o.subtitle ? ' — ' + o.subtitle : '')
                    return (
                      <label key={o.id} style={S.predCheckRow}>
                        <input type="checkbox" checked={ids.includes(o.id)} onChange={() => toggle(o.id)} />
                        <span style={S.predNumBadge}>{lineNumMap.get(o.id)}</span>
                        <span style={S.predCheckLabel}>{name}</span>
                      </label>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </ActionMenu>
    </>
  )
}

// The touch-time ladder, in days of hands-on work. Anything that feels like 13 is two rows.
const TOUCH_LADDER = [0.5, 1, 2, 3, 5, 8, 13]
const WAIT_LADDER = [1, 2, 3, 5, 8, 13, 21, 34, 55]   // waits, in calendar days

const LABEL_W = 780   // +80 for the Group column (2026-09-24)
const HEAD_H = 42

const MONO = '"DM Mono",monospace'
const S = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1400 },
  modal: { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '96vw', height: '93vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, zIndex: 1401, padding: '12px 18px', boxShadow: '0 18px 60px rgba(0,0,0,0.28)', fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text)', overflow: 'hidden' },
  headRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingBottom: 8, borderBottom: '0.5px solid var(--aj-border)' },
  kicker: { fontFamily: 'Montserrat,sans-serif', fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  x: { fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  sub: { fontSize: 11.5, color: 'var(--aj-text-muted)', marginTop: 4, lineHeight: 1.5, maxWidth: 640 },
  err: { marginTop: 8, fontSize: 12.5, color: '#c0392b' },
  body: { marginTop: 0, overflow: 'auto', flex: 1, position: 'relative' },
  muted: { fontSize: 12.5, color: 'var(--aj-text-muted)', padding: 12 },
  addBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#1a6fa8', background: 'none', border: '0.5px solid rgba(26,111,168,0.4)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', flexShrink: 0 },
  bufferReadout: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap' },
  inp: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', width: '100%', boxSizing: 'border-box' },
  // Wait toggle + touch-time input sit side by side (not stacked) so the cell fits in one ROW_H —
  // stacking them was the original crowding bug (2026-09-08).
  durCell: { display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center' },
  durInput: { width: 50, flexShrink: 0, padding: '3px 2px' },
  waitToggle: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, fontSize: 8, color: 'var(--aj-text-muted)', cursor: 'pointer', flexShrink: 0, lineHeight: 1 },
  waitToggleLbl: { textTransform: 'uppercase', letterSpacing: '0.02em', marginTop: 1 },
  // One-line summary button — opens the ActionMenu popover below. No inline chips, no
  // horizontal scrollbar (2026-09-08 crowding fix's chip strip was hard to use — Matt).
  predSummaryBtn: { width: '100%', height: '100%', boxSizing: 'border-box', textAlign: 'left', fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '3px 7px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-subtle)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  predPopover: { width: 280, maxHeight: 340, display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.18)', fontFamily: '"DM Sans",sans-serif' },
  predFilterInp: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 9px', border: 'none', borderBottom: '0.5px solid var(--aj-border)', background: 'transparent', color: 'var(--aj-text)', outline: 'none', boxSizing: 'border-box' },
  predPopoverList: { overflowY: 'auto', padding: '2px 0 6px' },
  predGroupLabel: { fontFamily: 'Montserrat,sans-serif', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', padding: '7px 9px 3px' },
  predCheckRow: { display: 'flex', alignItems: 'center', gap: 7, padding: '4px 9px', cursor: 'pointer', fontSize: 12, color: 'var(--aj-text)' },
  predNumBadge: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', width: 18, flexShrink: 0, textAlign: 'right' },
  predCheckLabel: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  delBtn: { fontSize: 16, color: '#b8540a', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 },
  btns: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--aj-border)' },
  ghost: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
  restricted: { fontSize: 13, color: 'var(--aj-text-muted)', padding: 20, textAlign: 'center' },
}
